import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { IonButton, IonIcon } from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import { calendarOutline, closeOutline, videocamOutline } from 'ionicons/icons';
import { catchError, interval, map, of, switchMap, timer } from 'rxjs';

import { UpcomingMeeting } from '../../../interfaces/upcoming-meeting.interface';
import { AuthUserService } from '../../../services/auth-user.service';
import { UpcomingMeetingsService } from '../../../services/infrastructure/google-workspace/upcoming-meetings.service';
import {
  meetingCountdownLabel,
  meetingNoticeKey,
  pickMeetingToAnnounce,
} from '../../../utils/upcoming-meeting.util';

/**
 * Cada cuánto se vuelve a leer el calendario. El aviso aparece 15 minutos
 * antes, así que en el peor caso una reunión agendada de último momento se
 * anuncia con 10 minutos de sobra.
 */
const POLL_INTERVAL_MS = 5 * 60 * 1000;

/** Reloj de la cuenta regresiva: el texto cambia aunque no llegue nada nuevo */
const CLOCK_TICK_MS = 15 * 1000;

/** Reuniones descartadas, para que no reaparezcan al recargar ni en otra pestaña */
const DISMISSED_STORAGE_KEY = 'terra_meeting_notice_dismissed';

/** Lo descartado se olvida un día después de que empezó la reunión */
const DISMISSED_TTL_MS = 24 * 60 * 60 * 1000;

/**
 * Lee las reuniones descartadas. El storage puede no estar disponible
 * (navegación privada, bloqueado): entonces simplemente no se recuerda.
 * @returns claves de `meetingNoticeKey`
 */
function readDismissed(): Set<string> {
  try {
    const raw = localStorage.getItem(DISMISSED_STORAGE_KEY);
    const keys: unknown = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(keys) ? keys.filter((k) => typeof k === 'string') : []);
  } catch {
    return new Set();
  }
}

/**
 * Guarda las descartadas, sin las de reuniones que ya pasaron hace más de un día.
 * @param keys - claves de `meetingNoticeKey`
 */
function writeDismissed(keys: ReadonlySet<string>): void {
  const cutoff = Date.now() - DISMISSED_TTL_MS;
  const recent = [...keys].filter((key) => new Date(key.split('|')[1] ?? '').getTime() > cutoff);
  try {
    localStorage.setItem(DISMISSED_STORAGE_KEY, JSON.stringify(recent));
  } catch {
    // Sin storage el descarte dura lo que dure la pestaña.
  }
}

/**
 * Aviso flotante "Próxima reunión": aparece 15 minutos antes de una reunión del
 * calendario de Google de quien tiene la sesión y se queda hasta 10 minutos
 * después de que empezó, con el botón para unirse.
 *
 * A diferencia del chip de pendientes por vencer, **sí se puede descartar**: la
 * reunión no es algo que Aurora le pida atender, y un aviso que no se quita en
 * 25 minutos estorba. Se descarta por ocurrencia, no por evento.
 *
 * Si Google no deja leer el calendario (o el ambiente aún no lo tiene
 * configurado), el back responde sin reuniones y el aviso nunca aparece.
 */
@Component({
  selector: 'app-upcoming-meeting-notice',
  templateUrl: './upcoming-meeting-notice.component.html',
  styleUrls: ['./upcoming-meeting-notice.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IonButton, IonIcon],
})
export class UpcomingMeetingNoticeComponent {
  private readonly meetingsService = inject(UpcomingMeetingsService);
  private readonly authUser = inject(AuthUserService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly meetings = signal<UpcomingMeeting[]>([]);
  private readonly dismissed = signal<ReadonlySet<string>>(readDismissed());

  /** Reloj propio de la cuenta regresiva */
  readonly now = signal(new Date());

  /** La reunión que toca anunciar, o `null` */
  readonly meeting = computed(() =>
    pickMeetingToAnnounce(this.meetings(), this.now(), this.dismissed()),
  );

  readonly countdown = computed(() => {
    const meeting = this.meeting();
    return meeting ? meetingCountdownLabel(meeting, this.now()) : '';
  });

  /** true si ya empezó: el aviso cambia de encabezado */
  readonly started = computed(() => {
    const meeting = this.meeting();
    return !!meeting && new Date(meeting.starts_at).getTime() <= this.now().getTime();
  });

  constructor() {
    addIcons({ calendarOutline, closeOutline, videocamOutline });

    // Arranca en cuanto hay sesión y se apaga al cerrarla, sin esperar la
    // siguiente vuelta del polling.
    toObservable(this.authUser.userId)
      .pipe(
        switchMap((userId) =>
          userId === null
            ? of<UpcomingMeeting[]>([])
            : timer(0, POLL_INTERVAL_MS).pipe(
                switchMap(() =>
                  this.meetingsService.getUpcomingMeetings().pipe(
                    map((response) => response.meetings),
                    catchError((err) => {
                      // Un fallo de red no apaga el aviso: se reintenta en la
                      // siguiente vuelta con la lista anterior.
                      console.error('❌ Error al consultar las próximas reuniones:', err);
                      return of(null);
                    }),
                  ),
                ),
              ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((meetings) => {
        if (meetings) {
          this.now.set(new Date());
          this.meetings.set(meetings);
        }
      });

    interval(CLOCK_TICK_MS)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.now.set(new Date()));
  }

  /** Oculta el aviso de esta reunión; el de la siguiente sí aparece */
  dismiss(): void {
    const meeting = this.meeting();
    if (!meeting) return;

    const next = new Set(this.dismissed());
    next.add(meetingNoticeKey(meeting));
    this.dismissed.set(next);
    writeDismissed(next);
  }
}
